import {Device} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Badge,
  COLORS,
  FONT_SIZES,
  FilterFlyoutIconName,
  GenericIconName,
  HeaderButton,
  PushNotificationIconName,
  Text,
  Toggle,
  useShowToast,
} from '@aboutyou/ui-components';
import {useUserTokenOrSessionId} from '@aboutyou/user';
import React, {useEffect} from 'react';
import sc from 'styled-components';
import {getUserPermissionsAndFirebaseToken} from '../../../backend-services/PushNotification/getUserPermissionsAndFirebaseToken';
import {usePushNotification} from '../../../backend-services/PushNotification/usePushNotification';
import {useBrowserSpecificUUID} from '../../BrowserSpecificUUID/useBrowserSpecificUUID';
import {DevicePushState} from '../../useDevicePushState/enums/DevicePushState';
import {useDevicePushState} from '../../useDevicePushState/useDevicePushState';
import {useShouldShowPushNotificationTrigger} from './hooks/useShouldShowPushNotificationTrigger';

export const InStreamPushNotificationToggle = () => {
  const {shouldShow, containerRef} = useShouldShowPushNotificationTrigger();

  const {updateDevicePushState, devicePushState} = useDevicePushState();
  const {updatePushNotification} = usePushNotification();
  const uuid = useBrowserSpecificUUID();
  const {data: tokenOrSessionId, isLoading} = useUserTokenOrSessionId();

  const updatePushNotificationByCase = (
    newState: DevicePushState,
    token?: string,
  ) => {
    if (
      newState === DevicePushState.SENT ||
      newState === DevicePushState.BLOCKED
    ) {
      updatePushNotification({
        device: Device.MOBILE_WEB,
        token: token || '',
        devicePushState: token ? 'granted' : 'denied',
        uuid,
        session: tokenOrSessionId,
      });
    }
  };

  useEffect(() => {
    const newState = updateDevicePushState();
    if (uuid && isLoading === false && tokenOrSessionId !== null) {
      if (newState === DevicePushState.SENT) {
        getUserPermissionsAndFirebaseToken().then(token => {
          // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
          updatePushNotificationByCase(newState, token);
        });
      } else {
        updatePushNotificationByCase(newState);
      }
    }
  }, [uuid, isLoading]);

  const handleClick = () => {
    getUserPermissionsAndFirebaseToken().then(token => {
      const newState = updateDevicePushState();
      updatePushNotificationByCase(newState, token);
    });
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Wrapper data-testid="pushNotificationTrigger" ref={containerRef}>
      <InStreamPushNotificationContent
        devicePushState={devicePushState}
        onToggle={handleClick}
      />
    </Wrapper>
  );
};

export const InStreamPushNotificationContent = (props: {
  onToggle: () => void;
  devicePushState?: DevicePushState;
}) => {
  const {t} = useTranslation();
  const showToast = useShowToast();

  const {onToggle, devicePushState} = props;

  useEffect(() => {
    if (devicePushState === DevicePushState.BLOCKED) {
      showToast({
        type: 'ERROR',
        content: t(
          'You need to accept push notifications in your browser settings in order to activate them.',
        ),
      });
    }
  }, [devicePushState]);

  return (
    <>
      <BodyWrapper>
        <Header>
          <StyledHeaderButton
            asset={GenericIconName.Bell}
            showBadge
            iconSize="S"
            badgeColor={COLORS.RedSale}
            aria-label={undefined}
          />
          <Text size="px16" weight="mediumHeavy">
            {t('Receive push notifications')}
          </Text>
        </Header>
        <Body>
          <Text size="px12">
            {t('Benefit from all our push notifications in the future:')}
          </Text>
          <BadgesWrapper>
            <StyledBadge
              text={t('Sales & Discounts')}
              svgIcon={{
                iconName: PushNotificationIconName.Gift,
                iconSize: 'XS',
              }}
              backgroundColor={COLORS.Grey4}
            />
            <StyledBadge
              text={t('Order and delivery status')}
              svgIcon={{
                iconName: GenericIconName.UspFreeShipping,
                iconSize: 'XS',
              }}
              backgroundColor={COLORS.Grey4}
            />
            <StyledBadge
              text={t('Product availability')}
              svgIcon={{
                iconName: FilterFlyoutIconName.Shirt,
                iconSize: 'XS',
              }}
              backgroundColor={COLORS.Grey4}
            />
          </BadgesWrapper>
          <Footer>
            <Text weight="bold">{t('Allow push notifications')}</Text>
            <Toggle
              onChange={onToggle}
              checked={devicePushState === DevicePushState.SENT}
              aria-label={
                devicePushState === DevicePushState.SENT
                  ? t('Reject push notifications')
                  : t('Allow push notifications')
              }
            />
          </Footer>
        </Body>
      </BodyWrapper>
      <LegalNote>
        <Text size="px10" color="Grey1">
          {t(
            'You can manage push notifications in your browser settings at any time',
          )}
        </Text>
      </LegalNote>
    </>
  );
};

const Wrapper = sc.div`
  margin: 2.5rem 1rem;
`;

const BodyWrapper = sc.div`
  border-radius: 0.5rem;
  border: 1px solid ${COLORS.Grey3};
  overflow: hidden;
`;

const Header = sc.div`
  display: flex;
  padding: 1rem 1.25rem;
  background: ${COLORS.Grey4};
  align-items: center;
  border-bottom: 1px solid ${COLORS.Grey7};
`;

const StyledHeaderButton = sc(HeaderButton)`
  margin: 0 0.75rem 0 0;
`;

const Body = sc.div`
  padding: 1rem 1.25rem 1.25rem;
`;

const BadgesWrapper = sc.div`
  display: flex;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledBadge = sc(Badge)`
  font-size: ${FONT_SIZES.px12};
`;

const Footer = sc.div`
  margin-top: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LegalNote = sc.div`
  margin-top: 0.75rem;
  text-align: center;
`;
